import "@/App.css";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  Apple,
  Play,
  Download,
  UserPlus,
  MousePointerClick,
  ShieldCheck,
  Lock,
  Star,
  ArrowRight,
  Smartphone,
  Sparkles,
  Timer,
  ScanLine,
  ChevronsDown,
} from "lucide-react";

const APPLE_URL =
  "https://apps.apple.com/gh/app/fxtm-trading-investment-app/id6738046376";
const GOOGLE_URL =
  "https://play.google.com/store/apps/details?id=com.exinity.fxtm";

const HERO_BG =
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmaW5hbmNlJTIwZ29sZCUyMGdyb3d0aCUyMHRyYWRpbmd8ZW58MHx8fHwxNzkwODkxMzI0fDA&ixlib=rb-4.1.0&q=85";
const APPLE_IMG =
  "https://images.unsplash.com/photo-1621761119692-595bf443cbc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHx0cmFkaW5nJTIwYXBwJTIwcGhvbmUlMjBtb2NrdXBzfGVufDB8fHx8MTc5MDg5MTMyNXww&ixlib=rb-4.1.0&q=85";
const GOOGLE_IMG =
  "https://images.unsplash.com/photo-1620207284057-e6c6b3d1b4c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx0cmFkaW5nJTIwYXBwJTIwcGhvbmUlMjBtb2NrdXBzfGVufDB8fHx8MTc5MDg5MTMyNXww&ixlib=rb-4.1.0&q=85";

const APPLE_THEME = {
  card: "border-sky-300/60 bg-gradient-to-b from-sky-400 via-blue-500 to-blue-700 hover:border-sky-200 hover:shadow-[0_0_50px_rgba(56,189,248,0.5)]",
  overlay: "via-blue-600/30 to-blue-700",
  ctaText: "text-blue-700",
};

const GOOGLE_THEME = {
  card: "border-emerald-300/60 bg-gradient-to-b from-green-400 via-emerald-500 to-teal-700 hover:border-emerald-200 hover:shadow-[0_0_50px_rgba(16,185,129,0.5)]",
  overlay: "via-emerald-600/30 to-teal-700",
  ctaText: "text-emerald-700",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const steps = [
  {
    n: "01",
    icon: MousePointerClick,
    title: "Choose Your Store",
    desc: "Tap the Apple App Store or Google Play card below.",
    testId: "step-1-card",
  },
  {
    n: "02",
    icon: Download,
    title: "Download the App",
    desc: "Install the official FXTM app on your phone in seconds.",
    testId: "step-2-card",
  },
  {
    n: "03",
    icon: UserPlus,
    title: "Sign Up & Claim $500",
    desc: "Register in the app to unlock your free $500 welcome bonus.",
    testId: "step-3-card",
  },
];

function CountdownTimer() {
  const [time, setTime] = useState({ h: 47, m: 59, s: 59 });

  useEffect(() => {
    const KEY = "fxtm_offer_deadline";
    let deadline = Number(localStorage.getItem(KEY));
    if (!deadline || deadline < Date.now()) {
      deadline = Date.now() + 48 * 60 * 60 * 1000;
      localStorage.setItem(KEY, String(deadline));
    }
    const tick = () => {
      const diff = Math.max(0, deadline - Date.now());
      setTime({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { value: time.h, label: "Hours" },
    { value: time.m, label: "Mins" },
    { value: time.s, label: "Secs" },
  ];

  return (
    <div
      data-testid="countdown-timer"
      className="mx-auto mt-8 inline-flex flex-col items-center gap-3"
    >
      <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-600">
        <Timer className="h-4 w-4" />
        Offer expires in
      </span>
      <div className="flex items-center gap-3">
        {units.map((u, i) => (
          <div key={u.label} className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <span className="min-w-[4.5rem] rounded-xl border border-red-300 bg-white px-4 py-3 font-mono text-2xl font-black text-red-600 shadow-[0_0_20px_rgba(220,38,38,0.15)] sm:text-3xl">
                {String(u.value).padStart(2, "0")}
              </span>
              <span className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-black">
                {u.label}
              </span>
            </div>
            {i < units.length - 1 && (
              <span className="-mt-6 font-mono text-2xl font-black text-red-400">
                :
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreCard({
  href,
  image,
  icon: Icon,
  store,
  badge,
  rating,
  downloads,
  cta,
  testId,
  delay,
  theme,
}) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      custom={delay}
      whileHover={{ y: -10 }}
      className={`group relative flex aspect-square flex-col overflow-hidden rounded-3xl border shadow-2xl transition-shadow duration-500 ${theme.card}`}
    >
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <img
          src={image}
          alt={`${store} preview`}
          className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-b from-transparent ${theme.overlay}`}
        />
        <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/50 px-3 py-1.5 backdrop-blur-md sm:left-5 sm:top-5 sm:gap-2 sm:px-4 sm:py-2">
          <Icon className="h-4 w-4 text-white sm:h-5 sm:w-5" />
          <span className="text-[11px] font-semibold tracking-wide text-white sm:text-sm">
            {store}
          </span>
        </div>
      </div>

      <div className="relative shrink-0 space-y-1.5 p-3 sm:space-y-4 sm:p-7">
        <p className="hidden text-[10px] font-semibold uppercase tracking-widest text-white/90 sm:block sm:text-xs">
          {badge}
        </p>
        <h3 className="font-display text-sm font-bold text-white sm:text-2xl">
          {store}
        </h3>
        <div className="hidden flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/80 sm:flex sm:gap-x-5 sm:text-sm">
          <span className="flex items-center gap-1.5">
            <Star className="h-4 w-4 fill-red-500 text-red-500" />
            {rating}
          </span>
          <span className="flex items-center gap-1.5">
            <Smartphone className="h-4 w-4 text-white/70" />
            {downloads}
          </span>
        </div>
        <div className="pt-1 sm:pt-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1.5 text-[10px] font-bold transition-transform duration-300 group-hover:scale-105 sm:gap-2 sm:px-5 sm:py-3 sm:text-sm ${theme.ctaText}`}
          >
            {cta}
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 sm:h-4 sm:w-4" />
          </span>
        </div>
        <div className="mt-5 hidden items-center gap-4 border-t border-white/25 pt-5 lg:flex">
          <div className="rounded-xl bg-white p-2">
            <QRCodeSVG value={href} size={72} level="M" />
          </div>
          <div className="text-left">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white">
              <ScanLine className="h-3.5 w-3.5" />
              Scan to download
            </p>
            <p className="mt-1 text-xs leading-relaxed text-white/70">
              Point your phone camera at the code to open the store instantly.
            </p>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/15 blur-3xl" />
    </motion.a>
  );
}

export default function App() {
  return (
    <div className="min-h-screen bg-white font-body text-black antialiased">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt=""
            className="h-full w-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/85 to-white" />
        </div>

        <div className="relative mx-auto max-w-5xl px-6 pb-20 pt-16 text-center sm:pt-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-red-400/50 bg-red-100 px-5 py-2"
            data-testid="limited-offer-badge"
          >
            <Sparkles className="h-4 w-4 text-red-500" />
            <span className="text-xs font-semibold uppercase tracking-widest text-red-600">
              Limited Time Welcome Offer
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display text-4xl font-extrabold leading-tight tracking-tight text-black sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            Get Your Free{" "}
            <span
              className="font-mono font-black text-black"
              data-testid="bonus-amount-display"
            >
              $500
            </span>
            <br className="hidden sm:block" /> Bonus
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-black sm:text-lg"
            data-testid="hero-subtext"
          >
            To claim your free $500 welcome bonus, simply download the official
            FXTM app and sign up. Choose your store below — it takes less than
            2 minutes to register and unlock your bonus.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2.5}>
            <CountdownTimer />
          </motion.div>

          {/* STEPS */}
          <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i + 4}
                data-testid={s.testId}
                className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <s.icon className="h-6 w-6 text-emerald-600" />
                  <span className="font-mono text-sm font-bold text-black">
                    {s.n}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-black">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-black">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STORE CARDS */}
      <section id="choose-store" className="relative px-6 pb-20">
        <div className="mx-auto max-w-5xl">
          <div
            className="mb-6 flex flex-col items-center gap-2"
            data-testid="scroll-to-stores-arrow"
          >
            <span className="text-xl font-bold uppercase tracking-widest text-red-600 sm:text-2xl">
              Your stores are below
            </span>
            <motion.div
              animate={{ y: [0, 14, 0] }}
              transition={{ repeat: Infinity, duration: 1.3, ease: "easeInOut" }}
            >
              <ChevronsDown className="h-16 w-16 text-red-600 sm:h-20 sm:w-20" />
            </motion.div>
          </div>

          <div className="mx-auto mt-12 grid grid-cols-2 gap-3 sm:gap-8">
            <StoreCard
              href={APPLE_URL}
              image={APPLE_IMG}
              icon={Apple}
              store="Apple App Store"
              badge="For iPhone & iPad"
              rating="4.8 (42.5K Ratings)"
              downloads="10M+ Downloads"
              cta="Download on the App Store"
              testId="apple-store-card"
              theme={APPLE_THEME}
              delay={0}
            />
            <StoreCard
              href={GOOGLE_URL}
              image={GOOGLE_IMG}
              icon={Play}
              store="Google Play Store"
              badge="For Android Devices"
              rating="4.7 (89.2K Reviews)"
              downloads="10M+ Downloads"
              cta="Get it on Google Play"
              testId="google-play-card"
              theme={GOOGLE_THEME}
              delay={1}
            />
          </div>

          {/* TRUST */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mx-auto mt-14 flex max-w-3xl flex-wrap items-center justify-center gap-x-10 gap-y-4"
            data-testid="trust-badge"
          >
            {[
              { icon: ShieldCheck, label: "Regulated & Secure" },
              { icon: Lock, label: "256-Bit Encryption" },
              { icon: Download, label: "10M+ Downloads Worldwide" },
            ].map((b) => (
              <span
                key={b.label}
                className="flex items-center gap-2 text-sm text-black"
              >
                <b.icon className="h-4 w-4 text-emerald-600" />
                {b.label}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-slate-200 px-6 py-8 text-center text-xs text-black">
        Download the app and sign up to qualify for the $500 welcome bonus.
        Terms and conditions apply. Trading involves risk.
      </footer>
    </div>
  );
}
